import { Mutex } from 'async-mutex'
import {
	BaseWorkflowTaskState,
	ForwardedFormPackageWorkflowTaskState,
	WorkflowTaskPermissions,
	WorkflowTaskStatus,
	WriteWorkflowTaskModel,
} from '../../api/clients/workflowTasks/DTOs'
import WorkflowTasksClient from '../../api/clients/workflowTasks/WorkflowTasksClient'
import {
	CloseFormPackageWorkflowEventData,
	OpenFormPackageWorkflowEventData,
	WorkflowEvent,
	WorkflowOutput,
	WorkflowStatus,
	WorkflowTaskEventAction,
	WorkflowTaskWorkflowEventData,
} from '../../api/clients/workflows/DTOs'
import WorkflowsClient from '../../api/clients/workflows/WorkflowsClient'
import { InteractiveWorkflowTaskResult } from '../../modules/Workflows/InteractiveWorkflowTasks/IInteractiveWorkflowTask'
import { setWorkflowTask } from '../../services/offline/WorkflowTaskHandling'
import { getActiveInteractiveTasks } from '../WorkflowTasks/InteractiveWorkflowTasks'

export enum FormPackageWorkflowResult {
	Closed = 0,
	NotClosed = 1,
	Canceled = 2,
	Failed = 3,
}

export type FormPackageWorkflowResultWithData = {
	result: FormPackageWorkflowResult
	data?: unknown
}

export type DisplayTaskParams = {
	canceledWorkflowTaskIds: number[]
	displayTask<T extends WriteWorkflowTaskModel<BaseWorkflowTaskState>>(
		task: T,
	): Promise<InteractiveWorkflowTaskResult> | undefined
	deletePackage: () => Promise<void>
	existingWorkflowTask?: WriteWorkflowTaskModel<ForwardedFormPackageWorkflowTaskState>
	onUpdateWorkflowTask?: (
		newTask: WriteWorkflowTaskModel<ForwardedFormPackageWorkflowTaskState>,
	) => void
}

const InteractiveWorkflowTaskMutex = new Mutex()

export async function handlePackageWorkflow(
	workflowInstanceId: number,
	params: DisplayTaskParams,
): Promise<FormPackageWorkflowResultWithData> {
	const workflowsClient = new WorkflowsClient()

	const { data: status } = await workflowsClient.getWorkflowStatus(
		workflowInstanceId,
	)

	const events = status.workflowEvents

	// if the form package is closed we're good to get out of this
	// reverse the events and make sure an open doesn't come after a close
	const isPackageClosed = (events: WorkflowEvent[]): FormPackageWorkflowResultWithData => {
		const reversedEvents = events.reverse()
		let result: FormPackageWorkflowResultWithData = {
			result: FormPackageWorkflowResult.NotClosed
		}

		for (const event of reversedEvents) {
			if (event.eventType === 'CloseFormPackage') result = { result: FormPackageWorkflowResult.Closed, data: event.eventData as CloseFormPackageWorkflowEventData }
			if (event.eventType === 'OpenFormPackage' && !result) return { result: FormPackageWorkflowResult.NotClosed, data: event.eventData as OpenFormPackageWorkflowEventData}
		}

		return result
	}

	const packageResult = isPackageClosed(events)

	if (packageResult.result === FormPackageWorkflowResult.Closed) {
		console.log('package is closed')

		return packageResult
	}

	console.log('package is not closed, handling user interaction')

	const taskResult = await displayTasks(params, status)
	return {
		result: taskResult
	}
}

const displayTasks = async (
	params: DisplayTaskParams,
	workflowOutput: WorkflowOutput,
): Promise<FormPackageWorkflowResult> => {
	if (InteractiveWorkflowTaskMutex.isLocked()) {
		console.log(
			'task mutex locked, skipping iteration to prevent tasks completed more than once',
		)
		return FormPackageWorkflowResult.NotClosed
	}

	const release = await InteractiveWorkflowTaskMutex.acquire()

	const workflowsClient = new WorkflowsClient()

	const tasks = await getActiveInteractiveTasks(workflowOutput.workflowEvents)

	for (const task of tasks) {
		const result = await params.displayTask(task)
		if (result === undefined) continue

		const { data: startedWorkflow } =
			await workflowsClient.createActivitySignal(
				task.declaredActivityInstanceId,
				result.signalTypeId,
				result.resources,
			)

		const { data: workflowStatus } = await workflowsClient.getWorkflowStatus(
			startedWorkflow.workflowInstanceId,
		)

		if (result.result === 'cancel' || workflowStatus.status === WorkflowStatus.Faulted) {
			// now stop the submission
			await params.deletePackage()

			// and now we need to update the workflow task so we can restart the
			// workflow with the activity that relies on the new workflow task

			const workflowTasksClient = new WorkflowTasksClient()

			// get workflow task created events
			// filter out the ones with the current task id, since we need the new task
			const workflowTaskEvents = workflowStatus.workflowEvents
				.filter((v) => v.eventType === 'WorkflowTask')
				.map((v) => v.eventData as WorkflowTaskWorkflowEventData)
				.filter(
					(v) => v.workflowTaskId !== params.existingWorkflowTask?.id && !params.canceledWorkflowTaskIds.includes(v.workflowTaskId),
				)

			// get only events where the task has only been created, not completed or removed
			const activeWorkflowTaskEvents = workflowTaskEvents.filter(
				(v) =>
					v.action === WorkflowTaskEventAction.Created &&
					workflowTaskEvents.find(
						(x) =>
							x.workflowTaskId === v.workflowTaskId &&
							(x.action !== WorkflowTaskEventAction.Created),
					) === undefined,
			)

			for (const event of activeWorkflowTaskEvents) {
				const { data: workflowTask } =
					await workflowTasksClient.getWorkflowTask(
						event.workflowTaskId,
						WorkflowTaskPermissions.Write,
					)

				if (workflowTask.workflowTaskStatus === WorkflowTaskStatus.Done)
					continue

				if (params.existingWorkflowTask !== undefined) {
					await workflowTasksClient.completeWorkflowTask(
						params.existingWorkflowTask.id,
						{
							lockPassword: params.existingWorkflowTask.lockPassword ?? '',
							results: {},
							logMessage:
								'Workflow task completed after interaction was canceled',
						},
					)
				}

				// get the new workflow task where this form package was forwarded
				if (
					workflowTask.workflowTaskState.workflowTaskTypeId ===
					'ForwardedFormPackage'
				) {
					params.onUpdateWorkflowTask &&
						params.onUpdateWorkflowTask(
							workflowTask as WriteWorkflowTaskModel<ForwardedFormPackageWorkflowTaskState>,
						)

					// add this new one to the cache
					setWorkflowTask(workflowTask)

					break
				}
			}

			release()

			if (workflowStatus.status === WorkflowStatus.Faulted)
				return FormPackageWorkflowResult.Failed

			if (result.result === 'cancel')
				return FormPackageWorkflowResult.Canceled
		}
	}

	release()
	return FormPackageWorkflowResult.NotClosed
}
